import React from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  Label,
  TextInput,
} from '../../components/ui';
import { verifyPublicRfq, type PublicRfqVerifyResponse } from '../../services/publicRfqApi';
import PublicRfqQuotationEditor from './PublicRfqQuotationEditor';

const PublicRfqPage: React.FC = () => {
  const { token: tokenParam } = useParams<{ token: string }>();
  const token = tokenParam ? decodeURIComponent(tokenParam) : '';
  const { colors } = useTheme();

  const [pin, setPin] = React.useState('');
  const [verifyBusy, setVerifyBusy] = React.useState(false);
  const [session, setSession] = React.useState<PublicRfqVerifyResponse | null>(null);

  const onVerify = async () => {
    if (!token || pin.trim().length < 6) {
      toast.error('Enter the PIN (at least 6 characters).');
      return;
    }
    setVerifyBusy(true);
    try {
      const res = await verifyPublicRfq(token, pin.trim());
      setSession(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not open this RFQ.');
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleBackToPin = () => {
    setSession(null);
  };

  const handleOfferSubmitted = () => {
    setSession(null);
    setPin('');
  };

  if (!token) {
    return (
      <div className={cn('flex min-h-screen items-center justify-center p-4', colors.surface)}>
        <p className={cn('text-sm', colors.textSecondary)}>Invalid link.</p>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen px-4 py-6 sm:px-6 lg:px-10 sm:py-8', colors.surface)}>
      <div className="mx-auto w-full max-w-[min(1920px,calc(100vw-2rem))]">
        {!session ? (
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              void onVerify();
            }}
          >
            <Card
              variant="outlined"
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
                'border shadow-sm',
                colors.border,
              )}
            >
              <div className="min-w-0 flex-1">
                <h1 className={cn('text-lg font-semibold leading-tight', colors.text)}>Supplier quotation</h1>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Enter your PIN to open the RFQ. You can save a draft or submit your final offer when ready.
                </p>
              </div>
              <Button type="submit" disabled={verifyBusy || pin.trim().length < 6} loading={verifyBusy} size="default">
                Continue
              </Button>
            </Card>

            <Card variant="default" className="p-4 md:p-5">
              <Accordion className="space-y-3">
                <AccordionItem
                  title="Access with PIN"
                  description="Required to open this RFQ."
                  defaultOpen
                  className={cn('overflow-hidden', colors.border, 'border')}
                  headerClassName={cn('!gap-2', colors.surfaceHover)}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-end">
                    <div className="md:col-span-4">
                      <Label htmlFor="rfq-pin">PIN</Label>
                      <TextInput
                        id="rfq-pin"
                        type="password"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        autoComplete="off"
                        placeholder="Enter PIN"
                        showClearButton={false}
                      />
                    </div>
                  </div>
                </AccordionItem>
              </Accordion>
            </Card>
          </form>
        ) : (
          <PublicRfqQuotationEditor
            token={token}
            pin={pin.trim()}
            quotation={session.quotation}
            currencies={session.currencies}
            units={session.units}
            onBack={handleBackToPin}
            onOfferSubmitted={handleOfferSubmitted}
            onQuotationUpdated={(q) => setSession((s) => (s ? { ...s, quotation: q } : s))}
          />
        )}
      </div>
    </div>
  );
};

export default PublicRfqPage;
